<template>
  <div class="chart-loading-wrapper">
    <div v-if="isLoading" class="chart-loading-skeleton">
      <div class="chart-loading-skeleton__header">
        <div class="chart-loading-skeleton__header-column header-column--left">
          <div class="skeleton-placeholder skeleton-placeholder--header-title"></div>
        </div>
        <div class="chart-loading-skeleton__header-column header-column--right">
          <div class="skeleton-placeholder skeleton-placeholder--header-value"></div>
        </div>
      </div>
      <div class="chart-loading-skeleton__content">
        <div class="chart-loading-skeleton__left-panel">
          <div
            v-for="itemIndex in 8"
            :key="`left-item-${itemIndex}`"
            class="chart-loading-skeleton__row chart-loading-skeleton__row--stacked"
          >
            <div
              class="skeleton-placeholder skeleton-placeholder--left-label"
              :style="{ width: getItemPlaceholderWidth('left', itemIndex) }"
            ></div>
            <div
              class="skeleton-placeholder skeleton-placeholder--left-sublabel"
              :style="{ width: '30%' }"
            ></div>
          </div>
        </div>
        <div class="chart-loading-skeleton__right-panel">
          <div
            v-for="itemIndex in 8"
            :key="`right-item-${itemIndex}`"
            class="chart-loading-skeleton__row chart-loading-skeleton__row--inline"
          >
            <div
              class="skeleton-placeholder skeleton-placeholder--right-label"
              :style="{ width: getItemPlaceholderWidth('right', itemIndex) }"
            ></div>
            <div
              class="skeleton-placeholder skeleton-placeholder--right-value"
              :style="{ width: '15%' }"
            ></div>
          </div>
        </div>
      </div>
      <div class="chart-loading-skeleton__pagination">
        <div class="skeleton-placeholder skeleton-placeholder--button"></div>
        <div class="skeleton-placeholder skeleton-placeholder--pagination-dots"></div>
        <div class="skeleton-placeholder skeleton-placeholder--button"></div>
      </div>
    </div>
    <slot v-else></slot>
  </div>
</template>

<script>
export default {
  props: {
    isLoading: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      leftPanelWidths: [80, 60, 90, 70, 50, 85, 65, 75],
      rightPanelWidths: [95, 85, 70, 90, 75, 80, 60, 99],
    };
  },
  methods: {
    /**
     * Retrieves a predefined width for a skeleton placeholder based on its panel and index.
     * Uses modulo to cycle through the respective width arrays, creating a predictable visual pattern.
     * @param {string} panelType - 'left' or 'right' to select the appropriate width array.
     * @param {number} index - The iteration index from v-for (1-based).
     * @returns {string} CSS width value (e.g., '80%').
     */
    getItemPlaceholderWidth(panelType, index) {
      const widths =
        panelType === 'left'
          ? this.leftPanelWidths
          : this.rightPanelWidths;
      return `${widths[(index - 1) % widths.length]}%`;
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-loading-wrapper {
  width: 100%;
  min-height: 250px;
  background-color: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.chart-loading-skeleton {
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
}

.skeleton-placeholder {
  background: linear-gradient(90deg, #F1EFEF -24.18%, #F9F8F8 50.26%, #E7E5E5 114.84%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: shimmer 1.4s infinite;
  height: 18px;
}

.chart-loading-skeleton__header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.chart-loading-skeleton__header-column {
  width: 50%;

  &.header-column--left {
    .skeleton-placeholder--header-title {
      width: 30%;
      height: 20px;
    }
  }

  &.header-column--right {
    display: flex;
    justify-content: flex-end;
    .skeleton-placeholder--header-value {
      width: 20%;
      height: 20px;
    }
  }
}

.chart-loading-skeleton__content {
  display: flex;
  margin-bottom: 20px;
}

.chart-loading-skeleton__left-panel,
.chart-loading-skeleton__right-panel {
  flex: 1;
  padding: 0 10px;
}

.chart-loading-skeleton__row {
  height: 62px;
  display: flex;
  justify-content: center;

  &--stacked {
    flex-direction: column;

    .skeleton-placeholder {
      border-radius: 9999px;
      height: 16px;

      &:not(:last-child) {
        margin-bottom: 5px;
      }
    }
  }

  &--inline {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .skeleton-placeholder {
      border-radius: 9999px;
    }
  }
}

.skeleton-placeholder--right-label {
  height: 8px;
}

.skeleton-placeholder--right-value {
  height: 16px;
  margin-left: 15px;
}

.chart-loading-skeleton__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;

  .skeleton-placeholder {
    &--button {
      width: 100px;
      height: 25px;
    }

    &--pagination-dots {
      width: 100px;
      height: 25px;
    }
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 768px) {
  .chart-loading-skeleton__content {
    flex-direction: column;
  }

  .chart-loading-skeleton__left-panel,
  .chart-loading-skeleton__right-panel {
    padding: 0;
  }
}
</style>
